import { z } from 'zod';
import { uuidSchema } from './common.schema.js';
import { TenantSeedUserSchema } from './cognito-user.schema';

// ─── Enums ───────────────────────────────────────────────────────────────────

export const TenantStatusEnum = z.enum([
  'PENDING_REVIEW',
  'PROVISIONING',
  'UPDATING',
  'DEPROVISIONING',
  'FAILED',
  'ACTIVE',
  'SUSPENDED',
  'DEPROVISIONED',
]);
export type TenantStatus = z.infer<typeof TenantStatusEnum>;

export const EnterpriseTypeEnum = z.enum(['CLINICAL', 'NON_CLINICAL', 'SAAS_ADMIN']);
export type EnterpriseType = z.infer<typeof EnterpriseTypeEnum>;

export const IsolationModelEnum = z.enum(['SILO', 'POOL', 'BRIDGE', 'NA']);
export type IsolationModel = z.infer<typeof IsolationModelEnum>;

export const IdpTypeEnum = z.enum([
  'NONE',
  'ENABLED',
  'SAML_AZURE_AD',
  'OIDC_AWS_SSO',
  'OIDC_OKTA',
]);
export type IdpType = z.infer<typeof IdpTypeEnum>;

export const BridgeLinkTransformationSchema = z.object({
  fieldMappings: z.record(z.string(), z.object({
    segment: z.string().min(1),
    field: z.string().min(1),
    component: z.string().min(1),
  })).optional(),
  valueMappings: z.record(z.string(), z.record(z.string(), z.string())).optional(),
  eventTypeMappings: z.record(z.string(), z.string()).optional(),
}).strict();
export type BridgeLinkTransformation = z.infer<typeof BridgeLinkTransformationSchema>;

const BridgelinkPortSchema = z.string()
  .regex(/^\d+$/, 'Port must be a whole number.')
  .refine((port) => {
    const portNumber = Number(port);
    return portNumber >= 1 && portNumber <= 65535;
  }, 'Port must be between 1 and 65535.');

const BridgelinkIpRangeSchema = z.string().refine((value) => {
  const [address, prefix, ...extraParts] = value.split('/');
  if (!address || extraParts.length > 0) return false;

  const isIpv4 = z.string().ipv4().safeParse(address).success;
  const isIpv6 = z.string().ipv6().safeParse(address).success;
  if (!isIpv4 && !isIpv6) return false;
  if (prefix === undefined) return true;
  if (!/^\d+$/.test(prefix)) return false;

  const prefixNumber = Number(prefix);
  return prefixNumber >= 0 && prefixNumber <= (isIpv4 ? 32 : 128);
}, 'IP range must be a valid IPv4 or IPv6 address, optionally with a valid CIDR prefix.');

// ─── Subscription Plan Entry ─────────────────────────────────────────────────

export const SubscriptionPlanEntrySchema = z.object({
  app_code: z.string(),
  plan_code: z.string(),
  tier_code: z.string().default('NA'),
  isolation_model: z.string().optional(),
});
export type SubscriptionPlanEntry = z.infer<typeof SubscriptionPlanEntrySchema>;

// ─── Cognito Config (nested map on TENANT item) ─────────────────────────────

export const CognitoConfigSchema = z.object({
  cognito_user_pool_id: z.string().optional(),
  primary_user_pool_id: z.string().optional(),
  region: z.string().optional(),
  client_id: z.string().optional(),
  domain: z.string().optional(),
  redirect_url: z.string().optional(),
});
export type CognitoConfig = z.infer<typeof CognitoConfigSchema>;

export const SubscriptionPlanSchema = z.record(z.string(), z.string());
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

// ─── Full Tenant Schema (DynamoDB item shape) ────────────────────────────────

export const TenantSchema = z.object({
  // Key fields
  hospital_id: uuidSchema,

  // Core attributes
  tenant_code: z.string().min(1),
  tenant_name: z.string().min(1),
  status: z.union([TenantStatusEnum, z.string()]),
  enterprise_type: EnterpriseTypeEnum.optional(),
  region_code: z.string().optional(),
  data_residency_zone: z.string().optional(),
  country_code: z.string().optional(),

  // GDPR
  gdpr_accepted: z.boolean().optional(),
  gdpr_accepted_at: z.string().optional(),
  gdpr_accepted_by: z.string().optional(),

  // PII (KMS-encrypted at application layer)
  primary_contact_name: z.string().optional(),
  primary_contact_email: z.string().email().optional(),
  primary_contact_phone: z.string().optional(),

  // Provisioning handles (nested Cognito config + flat fields)
  cognito: CognitoConfigSchema.optional(),
  cognito_user_pool_id: z.string().optional(),
  api_gateway_stage: z.string().optional(),
  schema_namespace: z.string().optional(),
  isolation_model: IsolationModelEnum.optional(),
  aws_account_id: z.string().optional(),

  // Enterprise link
  enterprise_id: uuidSchema.optional(),

  // Lifecycle
  deprovisioned_at: z.string().optional(),
  TTL: z.number().optional(),

  // Additional runtime attributes
  hospital_name: z.string().optional(),
  domain_identifier: z.string().optional(),
  tenant_hostname: z.string().optional(),
  tenant_legal_name: z.string().optional(),
  idp_type: IdpTypeEnum.optional(),
  plan_code: z.string().optional(),

  // Facility & operational attributes
  time_zone: z.string().optional(),
  facility_type: z.string().optional(),
  notes: z.string().optional(),
  subscription_plans: z.array(SubscriptionPlanSchema).optional(),

  // Language preferences (tenant-wide defaults)
  default_language: z.string().optional(),
  preferred_language: z.string().optional(),

  // Feature flags
  enable_bridgelink: z.boolean().optional(),
  /** Derived BridgeLink host URL; generated from tenant_code and tenant_hostname. */
  bridgelink_url: z.string().optional(),

  /** BridgeLink connection health, reported by the integration layer. Stored as-is from DynamoDB. */
  bridgelink_status: z.string().optional(),

  // Provisioning failure info
  failure_reason: z.string().optional(),
  is_retry_allowed: z.boolean().optional(),

  // Network / security configuration
  /** List of ports (submitted as multi-line textarea; stored as string array). */
  multiple_port: z.array(z.string()).optional(),
  /** List of allowed IP ranges / CIDR blocks (multi-line textarea). */
  ip_ranges: z.array(z.string()).optional(),
  /** Email addresses for operational alerts. */
  alert_email: z.array(z.string().email()).optional(),
  /** Content of the tenant transformation JSON file. */
  transformation: BridgeLinkTransformationSchema.optional(),
  /** Service account / API username (masked in the UI). */
  user_name: z.string().optional(),
  /** Service account / API password (masked in the UI). */
  password: z.string().optional(),

  // ── SEC (Single European Code) — Directive 2015/565 ──────────────────────
  // These three attributes are the sole source of truth for per-tenant SEC
  // configuration. They live only in DynamoDB (TENANT#<hospital_id> item);
  // no equivalent columns exist in the RDS service-plane tables.
  //
  // All reads MUST use null-coalescing for backwards compatibility — tenant
  // items created before SEC was introduced will not have these attributes.
  //   • item.sec_coding_system ?? 'E'
  //   • item.te_compendium_code ?? ''
  // If te_compendium_code is absent or empty, SEC generation MUST be blocked
  // with a clear error until the onboarding step is completed.

  /** Coding standard identifier. 'E' = EUTC (active); 'A' = ISBT 128 (future stub). Default: 'E'. */
  sec_coding_system: z.enum(['E', 'A']).optional().default('E'),
  /** Full ECDC TE code from the EU Compendium, e.g. 'BEGB123456'. 6 chars used in DIS (chars 3–8). */
  te_compendium_code: z.string().max(20).optional(),

  /** ICCBBA Facility Identification Number — required only for ISBT 128 path (sec_coding_system = 'A'). */
  fin_code: z.string().max(5).optional(),

  // Timestamps
  created_at: z.string(),
  updated_at: z.string(),
  onboarding_completed_at: z.string().optional(),
});

export type Tenant = z.infer<typeof TenantSchema>;

// ─── Create Tenant Request ───────────────────────────────────────────────────

export const CreateTenantRequestSchema = z.object({
  tenant_code: z.string().min(1).optional(),
  tenant_name: z.string().min(1),
  enterprise_type: EnterpriseTypeEnum.optional(),
  region_code: z.string().optional(),
  data_residency_zone: z.string().optional(),
  country_code: z.string().optional(),

  // GDPR
  gdpr_accepted: z.boolean().optional(),
  gdpr_accepted_by: z.string().optional(),

  // PII
  primary_contact_name: z.string().optional(),
  primary_contact_email: z.string().email().optional(),
  primary_contact_phone: z.string().optional(),

  // Provisioning handles
  cognito: CognitoConfigSchema.optional(),
  isolation_model: IsolationModelEnum.optional(),

  // Enterprise link
  enterprise_id: uuidSchema.optional(),

  // Additional runtime attributes
  hospital_name: z.string().optional(),
  domain_identifier: z.string().optional(),
  tenant_hostname: z.string().optional(),
  tenant_legal_name: z.string().optional(),
  idp_type: IdpTypeEnum.optional(),
  plan_code: z.string().optional(),

  // Facility & operational attributes
  time_zone: z.string().optional(),
  facility_type: z.string().optional(),
  notes: z.string().optional(),
  subscription_plans: z.array(SubscriptionPlanSchema).optional(),

  // Language preferences
  default_language: z.string().optional(),
  preferred_language: z.string().optional(),

  // Feature flags
  enable_bridgelink: z.boolean().optional(),

  // SEC (Single European Code) configuration
  sec_coding_system: z.enum(['E', 'A']).nullish(),
  te_compendium_code: z.string().max(20).nullish(),
  fin_code: z.string().max(5).nullish(),

  // Network / security configuration
  multiple_port: z.array(BridgelinkPortSchema).max(10, 'A maximum of 10 ports is allowed.'),
  ip_ranges: z.array(BridgelinkIpRangeSchema),
  alert_email: z.array(z.string().email()),
  transformation: BridgeLinkTransformationSchema,
  user_name: z.string().optional(),
  password: z.string().optional(),

  // Initial seed users (optional; same shape as Add User minus tenant_id/hospital_id)
  users: z.array(TenantSeedUserSchema).optional(),
});

export type CreateTenantRequest = z.infer<typeof CreateTenantRequestSchema>;

// ─── Update Tenant Request (partial) ─────────────────────────────────────────

export const UpdateTenantRequestSchema = z.object({
  tenant_name: z.string().min(1).optional(),
  status: TenantStatusEnum.optional(),
  enterprise_type: EnterpriseTypeEnum.optional(),
  region_code: z.string().optional(),
  data_residency_zone: z.string().optional(),
  country_code: z.string().optional(),

  // GDPR
  gdpr_accepted: z.boolean().optional(),
  gdpr_accepted_by: z.string().optional(),

  // PII
  primary_contact_name: z.string().optional(),
  primary_contact_email: z.string().email().optional(),
  primary_contact_phone: z.string().optional(),

  // Provisioning handles
  cognito: CognitoConfigSchema.optional(),
  cognito_user_pool_id: z.string().optional(),
  api_gateway_stage: z.string().optional(),
  schema_namespace: z.string().optional(),
  isolation_model: IsolationModelEnum.optional(),
  aws_account_id: z.string().optional(),

  // Enterprise link
  enterprise_id: uuidSchema.optional(),

  // Lifecycle
  deprovisioned_at: z.string().optional(),

  // Additional runtime attributes
  hospital_name: z.string().optional(),
  domain_identifier: z.string().optional(),
  tenant_hostname: z.string().optional(),
  tenant_legal_name: z.string().optional(),
  idp_type: IdpTypeEnum.optional(),
  plan_code: z.string().optional(),
  onboarding_completed_at: z.string().optional(),

  // Facility & operational attributes
  time_zone: z.string().optional(),
  facility_type: z.string().optional(),
  notes: z.string().optional(),
  subscription_plans: z.array(SubscriptionPlanSchema).optional(),

  // Language preferences
  default_language: z.string().optional(),
  preferred_language: z.string().optional(),

  // Feature flags
  enable_bridgelink: z.boolean().optional(),

  /** BridgeLink connection health, reported by the integration layer. */
  bridgelink_status: z.string().optional(),

  // ── SEC configuration (DynamoDB-only attributes) ─────────────────────────
  /** Coding standard: 'E' = EUTC, 'A' = ISBT 128. Written by onboarding/settings API. */
  sec_coding_system: z.enum(['E', 'A']).optional(),
  /** EU Compendium TE code, e.g. 'BEGB123456'. New SECs use the current value; historical SECs remain unchanged (Art. 10b(1)(c)). */
  te_compendium_code: z.string().max(20).optional(),
  /** ICCBBA FIN — required when sec_coding_system = 'A'. */
  fin_code: z.string().max(5).optional(),

  // Network / security configuration
  multiple_port: z.array(BridgelinkPortSchema).max(10, 'A maximum of 10 ports is allowed.').optional(),
  ip_ranges: z.array(BridgelinkIpRangeSchema).optional(),
  alert_email: z.array(z.string().email()).optional(),
  transformation: BridgeLinkTransformationSchema.optional(),
  user_name: z.string().optional(),
  password: z.string().optional(),
});

export type UpdateTenantRequest = z.infer<typeof UpdateTenantRequestSchema>;

// ─── Tenant List Query ───────────────────────────────────────────────────────

export const TenantListQuerySchema = z.object({
  status: z
    .union([TenantStatusEnum, z.array(TenantStatusEnum).min(1)])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type TenantListQuery = z.infer<typeof TenantListQuerySchema>;

// ─── EventBridge: Onboarding Request Event Detail ───────────────────────────

export const OnboardingRequestSchema = z.object({
  action: z.enum(['REGISTER', 'DEREGISTER']),
  tenantId: uuidSchema,
  tenantName: z.string().min(1),
  tenantModel: z.enum(['BASIC', 'STANDARD', 'PREMIUM']),
  tenantConfig: z.record(z.string(), z.unknown()).optional(),
  timestamp: z.string().datetime(),
});

export type OnboardingRequest = z.infer<typeof OnboardingRequestSchema>;

// ─── Tenant-code helper (merged from onboarding.schema) ─────────────────────

/**
 * Derive a DB-safe tenant_code from a tenant name.
 * - Uppercased, ASCII alphanumeric + hyphens only
 * - Collapses consecutive hyphens, strips leading/trailing hyphens
 * - Truncated to 50 chars (DB: @db.VarChar(50))
 */
export const TENANT_CODE_MAX_LENGTH = 50;
export const TENANT_CODE_PATTERN = /^[A-Z0-9]+(?:-[A-Z0-9]+)*$/;

export function toTenantCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-') // replace non-alphanumeric runs with single hyphen
    .replace(/^-|-$/g, '') // strip leading / trailing hyphens
    .slice(0, TENANT_CODE_MAX_LENGTH);
}

// ─── Create Tenant Response ──────────────────────────────────────────────────

export const CreateTenantResponseSchema = z.object({
  tenant_id: uuidSchema,
  tenant_registration_id: uuidSchema,
  tenant_code: z.string(),
  tenant_name: z.string(),
  tenant_status: TenantStatusEnum,
  isolation_model: IsolationModelEnum,
  created_at: z.string().datetime(),
});
export type CreateTenantResponse = z.infer<typeof CreateTenantResponseSchema>;

// ─── Update Tenant Settings Configured ──────────────────────────────────────

export const UpdateTenantSettingsConfiguredRequestSchema = z.object({
  is_settings_configured: z.boolean(),
});
export type UpdateTenantSettingsConfiguredRequest = z.infer<
  typeof UpdateTenantSettingsConfiguredRequestSchema
>;

export const UpdateTenantSettingsConfiguredResponseSchema = z.object({
  hospital_id: uuidSchema,
  is_settings_configured: z.boolean(),
  updated_at: z.string(),
});
export type UpdateTenantSettingsConfiguredResponse = z.infer<
  typeof UpdateTenantSettingsConfiguredResponseSchema
>;

// ─── BridgeLink Status ───────────────────────────────────────────────────────

export const BridgelinkStatusResponseSchema = z.object({
  hospital_id: uuidSchema,
  bridgelink_status: z.string(),
});
export type BridgelinkStatusResponse = z.infer<typeof BridgelinkStatusResponseSchema>;

// ─── BridgeLink Assets (certificates + user guide) ───────────────────────────

export const BridgelinkCertEntrySchema = z.object({
  /** Filename as stored in S3 (e.g. "keystore.p12", "ca.pem"). */
  fileName: z.string(),
  /** Pre-signed S3 download URL (1-hour TTL). */
  downloadUrl: z.string(),
  /** File size in bytes, null when unavailable. */
  size: z.number().nullable(),
  /** Last-modified timestamp from S3, null when unavailable. */
  lastModified: z.string().nullable(),
});
export type BridgelinkCertEntry = z.infer<typeof BridgelinkCertEntrySchema>;

export const BridgelinkAssetsResponseSchema = z.object({
  /** List of certificate files found under bl-certs/<tenantId>/. */
  certificates: z.array(BridgelinkCertEntrySchema),
  /** User guide PDF, or null when none is present under bl-certs/. */
  userGuide: z
    .object({
      fileName: z.string(),
      downloadUrl: z.string(),
    })
    .nullable(),
  /** BridgeLink Admin UI URL configured for the current environment. */
  bridgelinkUrl: z.string(),
  /** Whether BridgeLink is enabled for this tenant. */
  bridgelink_enabled: z.boolean(),
  /** MLLP ports configured for the tenant, sourced from DynamoDB (tenant.multiple_port). */
  ports: z.array(z.string()),
  /** Allowed BridgeLink IP ranges, sourced from DynamoDB (tenant.ip_ranges). */
  ipRanges: z.array(z.string()),
});
export type BridgelinkAssetsResponse = z.infer<typeof BridgelinkAssetsResponseSchema>;
/**
 * Read-back of the tenant's onboarding-complete flag.
 *
 * No `updated_at`: this is a projection of a single attribute, and the read path
 * has no business implying it knows when the whole tenant item last changed.
 */
export const TenantSettingsConfiguredResponseSchema = z.object({
  hospital_id: uuidSchema,
  is_settings_configured: z.boolean(),
});
export type TenantSettingsConfiguredResponse = z.infer<
  typeof TenantSettingsConfiguredResponseSchema
>;

/** Read-back of `ent_hospitals.hospital_name` for a tenant. */
export const TenantHospitalNameResponseSchema = z.object({
  hospital_id: uuidSchema,
  hospital_name: z.string(),
});
export type TenantHospitalNameResponse = z.infer<
  typeof TenantHospitalNameResponseSchema
>;

// ─── Onboarding Progress ─────────────────────────────────────────────────────

export const SETTINGS_WIZARD_STEP_KEYS = [
  'hospital-config',
  'roles-permissions',
  'allergy-management',
  'milk-type',
  'milk-formula-storage',
  'expiry-rules',
  'approval-compliance',
  'printer-config',
  'idp-federation',
  'interoperability-config',
  'localization',
] as const;

export type SettingsWizardStepKey = (typeof SETTINGS_WIZARD_STEP_KEYS)[number];

const wizardStepKeySchema = z.enum(SETTINGS_WIZARD_STEP_KEYS);

export const UpdateOnboardingProgressRequestSchema = z.object({
  step_key: wizardStepKeySchema.optional(),
  started: z.boolean().optional(),
}).refine((b) => b.step_key !== undefined || b.started !== undefined, {
  message: 'At least one of step_key or started must be provided.',
});
export type UpdateOnboardingProgressRequest = z.infer<
  typeof UpdateOnboardingProgressRequestSchema
>;

export const OnboardingProgressResponseSchema = z.object({
  hospital_id: uuidSchema,
  completed_steps: z.array(wizardStepKeySchema),
  started: z.boolean(),
  updated_at: z.string().optional(),
});
export type OnboardingProgressResponse = z.infer<
  typeof OnboardingProgressResponseSchema
>;
